import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FiSave, FiUpload, FiX, FiBriefcase, FiMapPin, FiPhone, FiMail, FiGlobe, FiFileText, FiDollarSign, FiImage } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTaxTerms from '../../hooks/useTaxTerms';
import { formatDate } from '../../utils/formatters';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const FormCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${colors.borderRadius.md};
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${props => props.$span ? `span ${props.$span}` : 'auto'};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};

  span {
    color: ${colors.error.main};
    margin-left: 2px;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.text.disabled};
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const LogoSection = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LogoPreview = styled.div`
  width: 150px;
  height: 150px;
  border: 2px dashed ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.neutral[50]};
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`;

const LogoPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${colors.text.disabled};

  svg {
    font-size: 40px;
  }

  span {
    font-size: 12px;
  }
`;

const RemoveLogoButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${colors.error.main};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s;

  ${LogoPreview}:hover & {
    opacity: 1;
  }
`;

const LogoActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LogoInfo = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.5;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[200]};
  }

  input {
    display: none;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

const CurrencyPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${colors.neutral[100]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.secondary};
`;

const MyCompany = () => {
  const { activeCompany, fetchCompany, switchCompany, currentUser } = useAuth();
  const { taxLabel, registrationLabel, registrationLabelFull, taxNumberLabel } = useTaxTerms();
  const { canEdit } = usePagePermissions('settings', 'myCompany');
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState({
    companyName: '',
    tradeName: '',
    address: '',
    city: '',
    province: '',
    country: 'Pakistan',
    postalCode: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    ntn: '',
    gstNo: '',
    stnNo: '',
    taxLabel: 'GST',
    logo: '',
    fiscalYearStart: 'July',
    currencyCode: 'PKR',
    currencySymbol: 'Rs.',
    decimalPlaces: 2,
    quantityDecimals: 2,
    dateFormat: 'dd MMM yyyy',
    whtOnInclusiveTax: false,
    whtLabel: '',
    whtAddToTotal: false,
    locationCompulsory: false
  });

  const pakistanProvinces = [
    'Punjab',
    'Sindh',
    'Khyber Pakhtunkhwa',
    'Balochistan',
    'Islamabad Capital Territory',
    'Gilgit-Baltistan',
    'Azad Jammu & Kashmir'
  ];

  const loadCompanyData = useCallback(() => {
    if (activeCompany) {
      setFormData({
        companyName: activeCompany.companyName || '',
        tradeName: activeCompany.tradeName || '',
        address: activeCompany.address || '',
        city: activeCompany.city || '',
        province: activeCompany.province || '',
        country: activeCompany.country || 'Pakistan',
        postalCode: activeCompany.postalCode || '',
        phone: activeCompany.phone || '',
        mobile: activeCompany.mobile || '',
        email: activeCompany.email || '',
        website: activeCompany.website || '',
        ntn: activeCompany.ntn || '',
        gstNo: activeCompany.gstNo || '',
        stnNo: activeCompany.stnNo || '',
        taxLabel: activeCompany.taxLabel || 'GST',
        logo: activeCompany.logo || '',
        fiscalYearStart: activeCompany.fiscalYearStart || 'July',
        currencyCode: activeCompany.currencyCode || 'PKR',
        currencySymbol: activeCompany.currencySymbol || 'Rs.',
        decimalPlaces: activeCompany.decimalPlaces ?? activeCompany.currency?.decimalPlaces ?? 2,
        quantityDecimals: activeCompany.quantityDecimals ?? activeCompany.currency?.quantityDecimals ?? 2,
        dateFormat: activeCompany.dateFormat || 'dd MMM yyyy',
        whtOnInclusiveTax: activeCompany.whtOnInclusiveTax === true,
        whtLabel: activeCompany.whtLabel || '',
        whtAddToTotal: activeCompany.whtAddToTotal === true,
        locationCompulsory: activeCompany.locationCompulsory === true
      });
    }
  }, [activeCompany]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  const handleLogoUpload = async (e) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert({ show: true, type: 'error', message: 'Please upload an image file' });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setAlert({ show: true, type: 'error', message: 'Image size should be less than 2MB' });
      return;
    }

    setUploading(true);
    try {
      // Create storage reference
      const storageRef = ref(storage, `companies/${activeCompany.id}/logo_${Date.now()}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Delete old logo if exists
      if (formData.logo) {
        try {
          const oldLogoRef = ref(storage, formData.logo);
          await deleteObject(oldLogoRef);
        } catch (err) {
          // Ignore error if old logo doesn't exist
          console.log('Could not delete old logo:', err);
        }
      }

      setFormData(prev => ({
        ...prev,
        logo: downloadURL
      }));

      setAlert({ show: true, type: 'success', message: 'Logo uploaded successfully' });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to upload logo. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (formData.logo) {
      try {
        const logoRef = ref(storage, formData.logo);
        await deleteObject(logoRef);
      } catch (err) {
        console.log('Could not delete logo:', err);
      }
    }
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    // Validation
    if (!formData.companyName.trim()) {
      setAlert({ show: true, type: 'error', message: 'Company name is required' });
      return;
    }

    if (!formData.address.trim()) {
      setAlert({ show: true, type: 'error', message: 'Address is required' });
      return;
    }

    if (!formData.city.trim()) {
      setAlert({ show: true, type: 'error', message: 'City is required' });
      return;
    }

    if (!formData.phone.trim()) {
      setAlert({ show: true, type: 'error', message: 'Phone number is required' });
      return;
    }

    if (!formData.email.trim()) {
      setAlert({ show: true, type: 'error', message: 'Email is required' });
      return;
    }

    setSaving(true);
    try {
      const companyRef = doc(db, 'companies', activeCompany.id);

      const updateData = {
        ...formData,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      };

      await updateDoc(companyRef, updateData);

      // Refresh company data in context
      await switchCompany(activeCompany.id);

      setAlert({ show: true, type: 'success', message: 'Company details saved successfully' });
    } catch (error) {
      console.error('Error saving company details:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save company details. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrencyPreview = () => {
    const amount = 1234567.89;
    const formatted = amount.toLocaleString('en-PK', {
      minimumFractionDigits: formData.decimalPlaces,
      maximumFractionDigits: formData.decimalPlaces
    });
    return `${formData.currencySymbol} ${formatted}`;
  };

  return (
    <PageLayout pageTitle="My Company">
      <Container>
        <PageHeader>
          <PageTitle>Company Settings</PageTitle>
          <PageDescription>
            Configure your company information, logo, tax numbers, and currency settings.
          </PageDescription>
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            {alert.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <FormCard>
            <CardHeader>
              <CardIcon>
                <FiBriefcase />
              </CardIcon>
              <CardTitle>Company Information</CardTitle>
            </CardHeader>
            <CardBody>
              <LogoSection>
                <LogoPreview>
                  {formData.logo ? (
                    <>
                      <img src={formData.logo} alt="Company Logo" />
                      {canEdit && (
                        <RemoveLogoButton type="button" onClick={handleRemoveLogo}>
                          <FiX />
                        </RemoveLogoButton>
                      )}
                    </>
                  ) : (
                    <LogoPlaceholder>
                      <FiImage />
                      <span>No Logo</span>
                    </LogoPlaceholder>
                  )}
                </LogoPreview>
                <LogoActions>
                  {canEdit && (
                    <UploadButton>
                      <FiUpload />
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploading}
                      />
                    </UploadButton>
                  )}
                  <LogoInfo>
                    Recommended size: 200x200 pixels<br />
                    Maximum file size: 2MB<br />
                    Formats: PNG, JPG, GIF
                  </LogoInfo>
                </LogoActions>
              </LogoSection>

              <div style={{ marginTop: '24px' }}>
                <FormGrid>
                  <FormGroup>
                    <Label>Company Name <span>*</span></Label>
                    <Input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Legal company name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Trade Name</Label>
                    <Input
                      type="text"
                      name="tradeName"
                      value={formData.tradeName}
                      onChange={handleChange}
                      placeholder="Trading / brand name"
                    />
                  </FormGroup>
                </FormGrid>
              </div>
            </CardBody>
          </FormCard>

          {/* Contact Information */}
          <FormCard>
            <CardHeader>
              <CardIcon>
                <FiMapPin />
              </CardIcon>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardBody>
              <FormGrid $cols={2}>
                <FormGroup $span={2}>
                  <Label>Address <span>*</span></Label>
                  <TextArea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Complete street address"
                    rows={2}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>City <span>*</span></Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City name"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Province</Label>
                  <Select
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                  >
                    <option value="">Select Province</option>
                    {pakistanProvinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Postal Code</Label>
                  <Input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    placeholder="e.g., 54000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone <span>*</span></Label>
                  <Input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+92-42-35761234"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Mobile</Label>
                  <Input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+92-300-1234567"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email <span>*</span></Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="info@company.com"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Website</Label>
                  <Input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="www.company.com"
                  />
                </FormGroup>
              </FormGrid>
            </CardBody>
          </FormCard>

          {/* Tax Information */}
          <FormCard>
            <CardHeader>
              <CardIcon>
                <FiFileText />
              </CardIcon>
              <CardTitle>Tax Information</CardTitle>
            </CardHeader>
            <CardBody>
              <FormGrid $cols={3}>
                <FormGroup>
                  <Label>{registrationLabel} ({registrationLabelFull})</Label>
                  <Input
                    type="text"
                    name="ntn"
                    value={formData.ntn}
                    onChange={handleChange}
                    placeholder={taxLabel === 'VAT' ? 'Commercial Registration number' : '1234567-8'}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{taxLabel} Registration Number</Label>
                  <Input
                    type="text"
                    name="gstNo"
                    value={formData.gstNo}
                    onChange={handleChange}
                    placeholder="Registration number"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{taxNumberLabel}{taxLabel === 'GST' ? ' (Sales Tax Number)' : ''}</Label>
                  <Input
                    type="text"
                    name="stnNo"
                    value={formData.stnNo}
                    onChange={handleChange}
                    placeholder={taxLabel === 'VAT' ? 'If different from VAT Registration' : 'If different from GST'}
                  />
                </FormGroup>
              </FormGrid>
            </CardBody>
          </FormCard>

          {/* Currency & Financial Settings */}
          <FormCard>
            <CardHeader>
              <CardIcon>
                <FiDollarSign />
              </CardIcon>
              <CardTitle>Currency & Financial Settings</CardTitle>
            </CardHeader>
            <CardBody>
              <FormGrid $cols={2}>
                <FormGroup>
                  <Label>Fiscal Year Start</Label>
                  <Select
                    name="fiscalYearStart"
                    value={formData.fiscalYearStart}
                    onChange={handleChange}
                  >
                    <option value="January">January</option>
                    <option value="April">April</option>
                    <option value="July">July</option>
                    <option value="October">October</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Currency</Label>
                  <Input value={`${activeCompany?.currencyCode || 'PKR'} (${activeCompany?.currencySymbol || 'Rs.'})`} disabled title="Managed by Super Admin" />
                </FormGroup>
                <FormGroup>
                  <Label>Amount Decimal Places</Label>
                  <Select
                    name="decimalPlaces"
                    value={formData.decimalPlaces}
                    onChange={handleNumberChange}
                  >
                    <option value={0}>0 - No decimals</option>
                    <option value={2}>2 - Standard (0.00)</option>
                    <option value={3}>3 - Extended (0.000)</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Quantity Decimal Places</Label>
                  <Select
                    name="quantityDecimals"
                    value={formData.quantityDecimals}
                    onChange={handleNumberChange}
                  >
                    <option value={0}>0 - Whole numbers only</option>
                    <option value={2}>2 - Standard (0.00)</option>
                    <option value={3}>3 - Extended (0.000)</option>
                    <option value={4}>4 - High precision (0.0000)</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Date Format</Label>
                  <Select
                    name="dateFormat"
                    value={formData.dateFormat}
                    onChange={handleChange}
                  >
                    <option value="dd MMM yyyy">15 Jul 2026</option>
                    <option value="dd/MM/yyyy">15/07/2026 (Day/Month/Year)</option>
                    <option value="MM/dd/yyyy">07/15/2026 (Month/Day/Year)</option>
                    <option value="yyyy-MM-dd">2026-07-15 (Year-Month-Day)</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Currency Preview</Label>
                  <CurrencyPreview>
                    {formatCurrencyPreview()}
                  </CurrencyPreview>
                </FormGroup>
                <FormGroup>
                  <Label>Date Preview</Label>
                  <CurrencyPreview>
                    {formatDate(new Date(), formData.dateFormat) || '—'}
                  </CurrencyPreview>
                </FormGroup>
                <FormGroup>
                  <Label>Withholding Tax (WHT) Base</Label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', padding: '9px 0' }}>
                    <input
                      type="checkbox"
                      name="whtOnInclusiveTax"
                      checked={formData.whtOnInclusiveTax}
                      onChange={e => setFormData(prev => ({ ...prev, whtOnInclusiveTax: e.target.checked }))}
                    />
                    Calculate WHT on tax-inclusive amount (including {formData.taxLabel || 'GST'})
                  </label>
                </FormGroup>
                <FormGroup>
                  <Label>WHT / Advance Tax Label</Label>
                  <Input
                    type="text"
                    name="whtLabel"
                    value={formData.whtLabel}
                    onChange={handleChange}
                    placeholder="WHT"
                    maxLength={40}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Shown on the Sale Invoice screen &amp; print (e.g. "Advance Tax 236G"). Blank = "WHT".
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>WHT on Sale Invoice total</Label>
                  <Select
                    name="whtAddToTotal"
                    value={formData.whtAddToTotal ? 'add' : 'deduct'}
                    onChange={e => setFormData(prev => ({ ...prev, whtAddToTotal: e.target.value === 'add' }))}
                  >
                    <option value="deduct">Deduct from total (withheld by customer)</option>
                    <option value="add">Add to total (advance tax collected, e.g. 236G / 236H)</option>
                  </Select>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    "Add" means the customer pays it on top and you owe it to FBR (booked as WHT/Tax Payable).
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Inventory Location</Label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', padding: '9px 0' }}>
                    <input
                      type="checkbox"
                      name="locationCompulsory"
                      checked={formData.locationCompulsory}
                      onChange={e => setFormData(prev => ({ ...prev, locationCompulsory: e.target.checked }))}
                    />
                    Location compulsory on documents
                  </label>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    When ticked, invoices, returns, orders and store documents can't be saved without selecting a Location.
                  </div>
                </FormGroup>
              </FormGrid>
            </CardBody>
            {canEdit && (
              <SaveButtonContainer>
                <SaveButton type="submit" disabled={saving || uploading}>
                  <FiSave />
                  {saving ? 'Saving...' : 'Save Changes'}
                </SaveButton>
              </SaveButtonContainer>
            )}
          </FormCard>
        </form>
      </Container>
    </PageLayout>
  );
};

export default MyCompany;
